// Evenfall cart drawer. Keeps the interface product-form.js and quick-add.js expect
// (open, close, renderContents, getSectionsToRender, setActiveElement) and
// re-renders the "cart-drawer" section after every quantity change.
// Wrapped in a guard because the theme includes this file twice.
(() => {
  if (customElements.get('cart-drawer')) return;

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  const sectionHTML = (html, selector) => {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.addEventListener('click', (event) => {
        if (event.target.closest('#CartDrawer-Overlay, [data-ecd-close]')) {
          this.close();
          return;
        }
        const qtyButton = event.target.closest('[data-ecd-qty]');
        if (qtyButton) {
          event.preventDefault();
          const line = qtyButton.closest('[data-line]');
          this.changeLine(line, Number(qtyButton.dataset.ecdQty));
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('active')) this.close();
      });

      this.bindCartIcon();
    }

    bindCartIcon() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.dataset.ecdBound) return;
      cartLink.dataset.ecdBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code === 'Space') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      document.documentElement.classList.add('ecd-lock');
      // a frame between showing and sliding so the transition always runs
      requestAnimationFrame(() => {
        this.classList.add('active');
        const panel = this.querySelector('.ecd__panel');
        if (typeof trapFocus === 'function') trapFocus(panel, panel);
        else panel.focus();
      });
    }

    close() {
      this.classList.remove('active');
      document.documentElement.classList.remove('ecd-lock');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    renderContents(parsedState) {
      this.renderSections(parsedState.sections);
      this.open();
    }

    renderSections(sections) {
      if (!sections) return;
      if (sections['cart-drawer']) {
        const html = sectionHTML(sections['cart-drawer'], '#CartDrawer');
        if (html !== null) this.querySelector('#CartDrawer').innerHTML = html;
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const html = sectionHTML(sections['cart-icon-bubble'], '.shopify-section');
        if (html !== null) bubble.innerHTML = html;
      }
      this.classList.toggle('is-empty', !!this.querySelector('.ecd__empty'));
      if (this.classList.contains('active') && typeof trapFocus === 'function') {
        const panel = this.querySelector('.ecd__panel');
        trapFocus(panel, panel);
      }
    }

    async changeLine(lineElement, quantity) {
      if (!lineElement || this.busy) return;
      this.busy = true;
      lineElement.classList.add('is-loading');
      const errorElement = this.querySelector('[data-ecd-error]');
      try {
        const response = await fetch(`${routes.cart_change_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(lineElement.dataset.line),
            quantity,
            sections: SECTIONS,
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'Could not update your cart.');
        this.renderSections(state.sections);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state });
        }
      } catch (error) {
        lineElement.classList.remove('is-loading');
        if (errorElement) {
          errorElement.textContent = error.message;
          errorElement.hidden = false;
        }
      } finally {
        this.busy = false;
      }
    }

    // Used by buttons outside a product form, e.g. the listicle page.
    async addVariant(variantId, quantity = 1, trigger) {
      if (trigger) this.setActiveElement(trigger);
      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
          sections: SECTIONS,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || 'Could not add to cart.');
      this.renderContents(state);
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  // Any link or button with data-ecd-add="<variant id>" adds that variant and opens the drawer.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ecd-add]');
    const drawer = document.querySelector('cart-drawer');
    if (!trigger || !drawer || typeof drawer.addVariant !== 'function') return;
    event.preventDefault();
    if (trigger.getAttribute('aria-busy') === 'true') return;
    trigger.setAttribute('aria-busy', 'true');
    drawer
      .addVariant(trigger.dataset.ecdAdd, Number(trigger.dataset.ecdQuantity || 1), trigger)
      .catch(() => {
        window.location.href = trigger.getAttribute('href') || window.routes.cart_url;
      })
      .finally(() => trigger.removeAttribute('aria-busy'));
  });
})();
